import type { AfterViewInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { PalantirAccordionComponent } from '../../../../accordion/accordion.component';
import { PalantirIconComponent } from '../../../../icon/icon.component';
import { PalantirInputComponent } from '../../../../input/input.component';
import { PalantirTextComponent } from '../../../../text/text.component';
import type { PalantirFilterModalInstance } from '../../../filter-modal.instance';
import type {
  PalantirFilterDateRangeInput,
  PalantirFilterInputFormType,
  PalantirFilterInputType,
} from '../../../filter-modal.type';
import { PalantirFilterModalErrorDisplayComponent } from '../../shared/error-display/error-display.component';
import { filterModalInputLogger } from '../logger.instance';

const logger = filterModalInputLogger.extend('date-range');

@Component({
  selector: 'palantir-filter-modal-date-range-input-component',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  standalone: true,
  imports: [
    PalantirAccordionComponent,
    PalantirInputComponent,
    ReactiveFormsModule,
    PalantirTextComponent,
    PalantirFilterModalErrorDisplayComponent,
    PalantirIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalantirFilterModalDateRangeInputComponent implements AfterViewInit {
  public readonly definition = input.required<PalantirFilterDateRangeInput>();

  public readonly controlGroup =
    input.required<PalantirFilterInputFormType[PalantirFilterInputType.DATE_RANGE]>();

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public readonly modalInstance = input.required<PalantirFilterModalInstance<{}>>();

  // eslint-disable-next-line unicorn/consistent-function-scoping
  public canShowFilter = computed(() => {
    const canShow = this.modalInstance().dependsOnFilterComputed(this.definition().dependsOn);
    logger.debug(`Visibility check for "${this.definition().label}":`, canShow);
    return canShow;
  });

  public constructor() {
    logger.debug('Component created');
  }

  public get fromControl(): FormControl {
    return this.controlGroup().get('from') as FormControl;
  }

  public get toControl(): FormControl {
    return this.controlGroup().get('to') as FormControl;
  }

  public ngAfterViewInit(): void {
    if (this.definition().forceDateOrder === false) {
      return;
    }

    this.fromControl.valueChanges.subscribe((from) => {
      if (from && this.toControl.value && from > this.toControl.value) {
        logger.debug(`Adjusting "to" date to match "from" date:`, from);
        this.toControl.setValue(from);
      }
    });

    this.toControl.valueChanges.subscribe((to) => {
      if (to && this.fromControl.value && to < this.fromControl.value) {
        logger.debug(`Adjusting "from" date to match "to" date:`, to);
        this.fromControl.setValue(to);
      }
    });
  }
}
